import * as THREE from "three";

import { Keyboard } from "./keyboard";

const MOVE_SPEED = 1.0;

class Camera {
  constructor() {
    this.position = new THREE.Vector3(107, 202, -154);
    this.direction = new THREE.Vector3(-0.76, -0.08, 0.65);

    const { yaw, pitch } = this.dirToYawPitch(this.direction);
    this.yaw = yaw;
    this.pitch = pitch;

    this.moveSpeed = MOVE_SPEED;
    this.mouseSensitivity = 0.002;

    document.addEventListener(
      "pointerlockchange",
      this.onPointerLockChange.bind(this),
      false,
    );
    document.addEventListener(
      "mozpointerlockchange",
      this.onPointerLockChange.bind(this),
      false,
    );

    document.addEventListener("mousemove", this.onMouseMove.bind(this), false);
    document.querySelector('canvas').addEventListener("click", this.toggleControls.bind(this), false);

    this.fov = 45.0;
    this.near = 0.00001;
    this.far = 1000000.0;

    this.controls_enabled = true
    this.keyboard_handler = new Keyboard();

    this.aspect_ratio = window.innerWidth / window.innerHeight;

    this.threeCamera = new THREE.PerspectiveCamera(
      this.fov,
      this.aspect_ratio,
      this.near,
      this.far
    );

    this.updateDirection();
    this.freeze();

    this.isPointerLocked = false;
  }

  syncThreeCamera()
  {
    this.threeCamera.position.copy(this.position);
    this.threeCamera.fov = this.fov;
    this.threeCamera.near = this.near;
    this.threeCamera.far = this.far;
    this.threeCamera.aspect = this.aspect_ratio;

    const lookAtTarget = this.position.clone().add(this.direction);

    this.threeCamera.up.set(0, 1, 0);
    this.threeCamera.lookAt(lookAtTarget);

    this.threeCamera.updateMatrixWorld(true);
    this.threeCamera.updateProjectionMatrix();
  }

  getFrustum() {
    const frustum = new THREE.Frustum();

    const matrix = new THREE.Matrix4().multiplyMatrices(
      this.threeCamera.projectionMatrix,
      this.threeCamera.matrixWorldInverse
    );

    frustum.setFromProjectionMatrix(matrix);

    return frustum;
  }

  getFrozenFrustum() {
    this.threeCamera.position.copy(this.positionFrozen);
    const lookAtTarget = this.positionFrozen.clone().add(this.directionFrozen);
    this.threeCamera.lookAt(lookAtTarget);

    this.threeCamera.updateMatrixWorld(true);
    this.threeCamera.updateProjectionMatrix();

    const frustum = new THREE.Frustum();

    const matrix = new THREE.Matrix4().multiplyMatrices(
      this.threeCamera.projectionMatrix,
      this.threeCamera.matrixWorldInverse
    );

    frustum.setFromProjectionMatrix(matrix);

    this.syncThreeCamera();

    return frustum;
  }

  freeze() {
    this.positionFrozen = new THREE.Vector3().copy(this.position);
    this.directionFrozen = new THREE.Vector3().copy(this.direction);
  }

  setPosition(pos) {
    this.position = pos;

    this.syncThreeCamera();
  }

  enableControls() {
    this.controls_enabled = true
  }

  disableControls() {
    this.controls_enabled = false;
  }

  getViewMatrix()
  {
    return this.threeCamera.matrixWorldInverse.clone();
  }

  getInverseViewMatrix()
  {
    return this.threeCamera.matrixWorld.clone();
  }

  getPerspectiveMatrix()
  {
    return this.threeCamera.projectionMatrix.clone();
  }

  getInversePerspectiveMatrix()
  {
    return this.threeCamera.projectionMatrixInverse.clone();
  }

  toggleControls() {
    if(this.controls_enabled) {
      if (!this.isPointerLocked) {
        this.lockControls();
      } else {
        this.unlockControls();
      }
    }
  }

  lockControls() {
    document.body.requestPointerLock();
  }

  unlockControls() {
    document.exitPointerLock();
  }

  onPointerLockChange() {
    this.isPointerLocked =
      document.pointerLockElement === document.body ||
      document.mozPointerLockElement === document.body;
  }

  onMouseMove(event) {
    if (this.isPointerLocked && this.controls_enabled) {
      this.yaw -= event.movementX * this.mouseSensitivity;
      this.pitch += event.movementY * this.mouseSensitivity;

      // Limit vertical rotation (-π/2 to π/2)
      this.pitch = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, this.pitch));

      this.updateDirection();
    }
  }

  dirToYawPitch(dir) {
    const d = dir.clone().normalize();
    const pitch = Math.asin(-d.y);
    const yaw = Math.atan2(d.z, -d.x);

    return { yaw, pitch };
  }

  setDirection(dir) {
    const { yaw, pitch } = this.dirToYawPitch(dir);
    this.yaw = yaw;
    this.pitch = pitch;

    this.updateDirection();
  }

  updateDirection() {
    // Calculate new direction based on mouse movement
    this.direction = new THREE.Vector3(
      -Math.cos(this.pitch) * Math.cos(this.yaw),
      -Math.sin(this.pitch),
      Math.cos(this.pitch) * Math.sin(this.yaw),
    ).normalize();

    this.syncThreeCamera();
  }

  updateMovement() {
    let moveVector = new THREE.Vector3(0.0, 0.0, 0.0);

    // Calculate movement vectors in all three axes
    const forwardVector = new THREE.Vector3(
      this.direction.x,
      0,
      this.direction.z,
    ).normalize();
    const rightVector = new THREE.Vector3(
      -this.direction.z,
      0,
      this.direction.x,
    ).normalize();
    const upVector = new THREE.Vector3(0, 1.0, 0);

    if(this.controls_enabled) {
      // Accumulate movement based on moveState
      if (this.keyboard_handler.isDown({"key": "w"})) {
        moveVector.add(forwardVector);
      }
      if (this.keyboard_handler.isDown({"key": "s"})) {
        moveVector.sub(forwardVector);
      }
      if (this.keyboard_handler.isDown({"key": "a"})) {
        moveVector.sub(rightVector);
      }
      if (this.keyboard_handler.isDown({"key": "d"})) {
        moveVector.add(rightVector);
      }
      if (this.keyboard_handler.isDown({"key": " "})) {
        moveVector.add(upVector);
      }
      if (this.keyboard_handler.isDown({"key": "c"})) {
        moveVector.sub(upVector);
      }
      if (this.keyboard_handler.isDown({ "key": "f" })) {
        this.freeze();
      }
      if (this.keyboard_handler.isDown({"key": "Shift"})) {
        this.moveSpeed = 3.0 * MOVE_SPEED;
      } else {
        this.moveSpeed = MOVE_SPEED;
      }
    }

    // Calculate distance to the point (0.5, 0.5, 0.5)
    const targetPoint = new THREE.Vector3(0.5, 0.5, 0.5);
    const distanceToTarget = this.position.distanceTo(targetPoint);

    // Normalize and apply movement speed
    moveVector.normalize().multiplyScalar(this.moveSpeed);

    // Update position
    this.position.add(moveVector);

    this.syncThreeCamera();
  }

  lerpMatrix(mat1, mat2, t) {
    const result = new THREE.Matrix4();

    // Linearly interpolate each element of the matrices
    for (let i = 0; i < 16; i++) {
      result.elements[i] = mat1.elements[i] * (1 - t) + mat2.elements[i] * t;
    }

    return result;
  }

  makeRay(mousePosition) {
    const canvas = document.querySelector('canvas');

    const rect = canvas.getBoundingClientRect();

    const x = mousePosition.x - rect.left;
    const y = mousePosition.y - rect.top;

    const ndc = new THREE.Vector4(
      (x / canvas.clientWidth) * 2.0 - 1.0,
      -(y / canvas.clientHeight) * 2.0 + 1.0,
      -1.0,
      1.0
    );

    ndc.applyMatrix4(this.threeCamera.projectionMatrixInverse);

    ndc.z = -1.0;
    ndc.w = 0.0;

    ndc.applyMatrix4(this.threeCamera.matrixWorld);

    const origin = this.position.clone();

    const direction = new THREE.Vector3(
      ndc.x,
      ndc.y,
      ndc.z
    ).normalize();

    return new THREE.Ray(origin, direction);
  }

  getUniforms()
  {
    this.syncThreeCamera();

    const uniforms = {};

    uniforms.cameraPosition = this.position.toArray();
    uniforms.cameraDirection = this.direction.toArray();

    uniforms.cameraPositionFrozen = this.positionFrozen.toArray();
    uniforms.cameraDirectionFrozen = this.directionFrozen.toArray();

    uniforms.cameraFov = this.fov;

    uniforms.projectionMatrix = this.threeCamera.projectionMatrix.toArray();
    uniforms.invProjectionMatrix = this.threeCamera.projectionMatrixInverse.toArray();

    uniforms.viewMatrix = this.threeCamera.matrixWorldInverse.toArray();
    uniforms.invViewMatrix = this.threeCamera.matrixWorld.toArray();

    return uniforms;
  }
}

export default Camera;
